import { useState, useMemo } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import useGrecaptcha from '@/hooks/useGrecaptcha';
import { passwordApi } from '@/api';

interface Inputs {
  emailAddress: string;
}

export const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useResetPasswordForm = (prefillIdentification?: string | null) => {
  const { t, i18n } = useTranslation();
  const executeGrecaptch = useGrecaptcha();
  const [prefill, setPrefill] = useState<string | null>(prefillIdentification ?? null);
  const [isRequestSent, setIsRequestSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const language = useMemo(() => i18n.language, [i18n.language]);

  const schema = yup.object().shape({
    emailAddress: yup
      .string()
      .required(t('resetPasswordForm.validation.required'))
      .matches(
        emailPattern,
        t('resetPasswordForm.validation.emailAddress'),
      ),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
    getValues,
  } = useForm<Inputs>({
    criteriaMode: 'all',
    resolver: yupResolver(schema),
  });

  const sendReset = async (emailAddress: string) => {
    try {
      setIsLoading(true);
      const recaptchaToken = await executeGrecaptch();
      await passwordApi.resetPassword({ emailAddress, recaptchaToken }, language);
      setIsRequestSent(true);
      setApiError(null);
    } catch (err) {
      const error = err as AxiosError<any>;

      if (error?.response?.data?.Reasons?.length > 0) {
        const reasons = error?.response?.data.Reasons as string[];
        setError('emailAddress', {
          type: 'server',
          message: reasons[0],
          types: reasons.reduce<Record<string, string>>((acc, item, index) => (
            { ...acc, [`server${index}`]: item }
          ), {}),
        });
      } else {
        return t('changePasswordForm.generalError');
      }
      setApiError((error as Error)?.message || t('changePasswordForm.generalError'));
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit: SubmitHandler<Inputs> = ({ emailAddress }) => sendReset(emailAddress);

  const confirmPrefill = () => {
    if (prefill) {
      sendReset(prefill);
    }
  };

  const resetFormState = () => {
    reset();
    setPrefill(null);
    setApiError(null);
    setIsRequestSent(false);
  };

  const identification = prefill ?? getValues().emailAddress;

  return {
    register,
    handleSubmit,
    onSubmit,
    confirmPrefill,
    errors,
    isRequestSent,
    resetFormState,
    isLoading,
    apiError,
    prefill,
    identification,
    isEmailIdentification: emailPattern.test(identification ?? ''),
  };
};

export default useResetPasswordForm;
